import { useMemo } from 'react';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import type { MemberRole, MemberRoleMap } from '../utils/constants';

import { useMemberEdit } from './useMemberEdit';
import { useRoles } from './useRoles';

// Stable fallback: useMemberEdit re-seeds its selections when this changes.
const EMPTY_ROLES_BY_PRODUCT: Record<string, MemberRoleMap[]> = {};

/**
 * The single-entity access group of the edit member tray: `useMemberEdit` seeded
 * with the defined roles in `memberRoles`, for the organization's only legal entity.
 * `SingleLEEditTray` and `PendingInviteRoles` share it so both render the same rows.
 */
export function useSingleLEMemberAccess(memberRoles: readonly MemberRole[]) {
  const { selectedOrganization } = useOrganization();
  const { roleMap, rolesByProductPerEntity } = useRoles();
  const isHierarchicalRolesTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const leRolesByProduct =
    rolesByProductPerEntity[legalEntityIds[0] ?? ''] ?? EMPTY_ROLES_BY_PRODUCT;

  // Every defined role the member holds is seeded into the editable selection so
  // it starts checked. Assignable roles stay toggleable, unassignable roles
  // render checked + disabled.
  const definedMemberRoles = useMemo(
    () => memberRoles.filter((role) => roleMap[role] !== undefined),
    [memberRoles, roleMap],
  );

  const memberEdit = useMemberEdit({
    memberRoles: definedMemberRoles,
    rolesByProduct: leRolesByProduct,
    roleMap,
    isHierarchicalRolesTreatment,
  });

  return { ...memberEdit, legalEntityIds };
}
